/**
 * Auth contracts — DESIGN.md §9.1, §12.2.
 */
import { z } from 'zod';
import { EMAIL_MODES, NOTIFICATION_KINDS, USER_ROLES } from '../enums.js';
import { AVATAR_COLOURS } from '../domain/avatar-colour.js';
import { emailSchema, name, uuidSchema } from './common.js';
import { relativeReminderSchema } from './reminders.js';
/**
 * Password policy. Length beats composition rules for an internal tool whose
 * primary path is Google SSO (§9.1); the floor exists so the fallback is not
 * the weak link.
 */
export const passwordSchema = z
    .string()
    .min(12, 'Use at least 12 characters')
    .max(200, 'That password is too long');
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(200),
});
export const acceptInviteRequestSchema = z.object({
    token: z.string().min(10).max(500),
    password: passwordSchema,
    name: name(200).optional(),
});
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: passwordSchema,
});
/** The authenticated user as the SPA sees it. */
export const meSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    name: z.string(),
    avatarUrl: z.string().nullable(),
    /** #53 — null means the default derived from `id`. See `avatarColorSchema`. */
    avatarColor: z.string().nullable(),
    role: z.enum(USER_ROLES),
    timezone: z.string(),
    isActive: z.boolean(),
    groups: z.array(z.object({ id: uuidSchema, name: z.string() })),
    hasPassword: z.boolean(),
    hasGoogle: z.boolean(),
    notificationPreferences: z.object({
        emailMode: z.enum(EMAIL_MODES),
        instantForMentions: z.boolean(),
        instantForAssignments: z.boolean(),
        mutedKinds: z.array(z.enum(NOTIFICATION_KINDS)),
        defaultReminder: relativeReminderSchema.nullable(),
    }),
    createdAt: z.string(),
});
export const updateMeRequestSchema = z
    .object({
    name: name(200).optional(),
    timezone: z.string().min(1).max(64).optional(),
    avatarUrl: z.string().url().max(2048).nullish(),
    /**
     * #53 — the colour behind your initials. `null` puts you back on the
     * default derived from your id, which is a real choice rather than a way
     * of saying "unset": it is what everyone starts with.
     */
    avatarColor: z.enum(AVATAR_COLOURS).nullish(),
})
    .strict();
export const notificationPreferencesRequestSchema = z
    .object({
    emailMode: z.enum(EMAIL_MODES),
    instantForMentions: z.boolean(),
    instantForAssignments: z.boolean(),
    mutedKinds: z.array(z.enum(NOTIFICATION_KINDS)).max(NOTIFICATION_KINDS.length),
})
    .strict();
/**
 * Notification preferences — DESIGN.md §11.2, §12.2 (`PUT
 * /me/notification-preferences`).
 *
 * R23's defaults are the column defaults; this is how a user changes them.
 * `mutedKinds` is the per-kind opt-out the fan-out honours (§11.1).
 */
export const updateNotificationPreferencesRequestSchema = z
    .object({
    emailMode: z.enum(EMAIL_MODES).optional(),
    instantForMentions: z.boolean().optional(),
    instantForAssignments: z.boolean().optional(),
    mutedKinds: z.array(z.enum(NOTIFICATION_KINDS)).max(NOTIFICATION_KINDS.length).optional(),
    /** #99 — null switches it off. */
    defaultReminder: relativeReminderSchema.nullable().optional(),
})
    .strict();
export const notificationPreferencesSchema = z.object({
    emailMode: z.enum(EMAIL_MODES),
    instantForMentions: z.boolean(),
    instantForAssignments: z.boolean(),
    mutedKinds: z.array(z.enum(NOTIFICATION_KINDS)),
    /**
     * #99 — a reminder relative to the due date on every card assigned to you
     * that has one. Null when you have none.
     */
    defaultReminder: relativeReminderSchema.nullable(),
});
//# sourceMappingURL=auth.js.map